import type {
  IAccountServiceOptions,
  IAccountServiceUser,
  ISaveServiceOptions
} from '@inter-ikea-kompis/services';
import {
  SaveService,
  StoreIdCookieInformationService,
  ZipCodeCookieInformationService
} from '@inter-ikea-kompis/services';
import { type DexfSettingsFilterSegmentEnum } from '@inter-ikea-kompis/enums';
import { getRange } from '@inter-ikea-storageone/shared/utils/queryUtils';
import { stripURLOfQueryParameters } from '@inter-ikea-storageone/shared/utils/queryUtils';
import { getLocale } from '@inter-ikea-storageone/shared/utils/i18nUtils';
import type { IStorageOneDexfSettings } from '@inter-ikea-storageone/shared/types';
import type { Range } from '@inter-ikea-storageone/range';
import { PlannerAccountService } from './PlannerAccountService';
import { getDexfApiKey } from '@inter-ikea-storageone/shared/services/dexfApiKey';

export const isUserTokenExpired = (user: IAccountServiceUser) => {
  const expiry = getTokenExpiry(user);
  if (!expiry) {
    return false;
  }
  return expiry.getTime() <= Date.now();
};

export const getTokenExpiry = (user: IAccountServiceUser): Date | null => {
  if (user.cocreateUser) {
    // Note: cocreate uses 4 hour TTL but only a UUID for the accessToken so we don't know the precise time of start
    //  so this might be marginally longer than reality
    return new Date(Date.now() + 4 * 60 * 60 * 1e3);
  }
  try {
    return new Date(JSON.parse(atob(user.accessToken.split('.')[1])).exp * 1e3);
  } catch {
    //
  }
  return null;
};

export const getDefaultRedirectUri = () => stripURLOfQueryParameters(['state', 'code']);

export const createSaveService = (
  dexfSettings: IStorageOneDexfSettings,
  dexfSettingsFilterSegment: DexfSettingsFilterSegmentEnum
) => {
  const range = getRange() as Range;
  const dexfApiKey = getDexfApiKey(range);
  const applicationName = `storageone${range}.${dexfSettingsFilterSegment}`;
  const dexfApplicationId = `storageone${range}`;

  //As of now we need to send in what segment we want to use in oAuth in DEXF Settings in the applicationName prop
  const saveServiceOptions: ISaveServiceOptions = {
    applicationName: applicationName,
    dexfApplicationId: dexfApplicationId,
    settings: dexfSettings,
    dexfApiKey
  };
  return new SaveService(saveServiceOptions);
};

export const createAccountService = (
  dexfSettings: IStorageOneDexfSettings,
  dexfSettingsFilterSegment: DexfSettingsFilterSegmentEnum
) => {
  const range = getRange();
  const locale = getLocale();
  const applicationName = `storageone${range}.${dexfSettingsFilterSegment}`;
  const dexfApiKey = getDexfApiKey(range);

  //As of now we need to send in what segment we want to use in oAuth in DEXF Settings in the applicationName prop
  const accountServiceSettings: IAccountServiceOptions = {
    applicationName: applicationName,
    redirectUri: getDefaultRedirectUri(),
    logoutRedirectUri: getDefaultRedirectUri(),
    settings: dexfSettings,
    locale,
    dexfApiKey
  };
  return new PlannerAccountService(accountServiceSettings);
};

export const createZipCodeService = (dexfSettings: IStorageOneDexfSettings) => {
  return new ZipCodeCookieInformationService({ settings: dexfSettings });
};

export const createStoreIdCookieInformationService = (dexfSettings: IStorageOneDexfSettings) => {
  return new StoreIdCookieInformationService({ settings: dexfSettings });
};
